import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { CSSProperties, type FC, ForwardRefExoticComponent, type JSX, KeyboardEventHandler, ReactNode, RefAttributes, createContext, forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Card } from '../Card';
import { IconButton } from '../IconButton';
import { LayoutStack } from '../layouts';
import { LoadingSkeleton } from '../LoadingSkeleton';
import { Keys } from '../Popup';
import { Tooltip } from '../Tooltip';
import styles from './DonationFeedItem.module.css';
import { AmountContainerProps, DetailsContainerProps, DonationFeedItemCompanionProps, DonationFeedItemProps } from './types';
type SlotComponent = FC<DonationFeedItemCompanionProps>;
type AmountComponent = FC<AmountContainerProps>;
type DetailsComponent = FC<DetailsContainerProps>;
type FirstSlotComponent = SlotComponent & {
  AmountContainer: AmountComponent;
};
type SecondSlotComponent = SlotComponent & {
  DetailsContainer: DetailsComponent;
};
type DonationFeedItemComponent = ForwardRefExoticComponent<DonationFeedItemProps & RefAttributes<HTMLDivElement>> & {
  FirstSlot: FirstSlotComponent;
  SecondSlot: SecondSlotComponent;
  ContextProvider: ({
    children
  }: {
    children: ReactNode;
  }) => JSX.Element;
};
function AmountSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <LoadingSkeleton width="6.25rem" height="3.523rem" className={styles.skeletonContainer} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function AmountContainer(t0) {
  const $ = _c(3);
  const {
    amount,
    skeleton: t1
  } = t0;
  const skeleton = t1 === undefined ? false : t1;
  if (skeleton) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <AmountSkeleton />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  let t2;
  if ($[1] !== amount) {
    t2 = <div className={styles.amountContainer}>{amount}</div>;
    $[1] = amount;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function FirstSlot(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
FirstSlot.AmountContainer = AmountContainer;
const COMMENT_PREVIEW_LINES = 1;
function useDonationFeedItemContext(id) {
  const $ = _c(6);
  const {
    toggle,
    expanded
  } = useContext(DonationFeedItemContext);
  const isExpanded = !!expanded[id];
  let t0;
  if ($[0] !== id || $[1] !== toggle) {
    t0 = () => toggle(id);
    $[0] = id;
    $[1] = toggle;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const toggleExpanded = t0;
  let t1;
  if ($[3] !== isExpanded || $[4] !== toggleExpanded) {
    t1 = {
      isExpanded,
      toggleExpanded
    };
    $[3] = isExpanded;
    $[4] = toggleExpanded;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function CommentInner(t0) {
  const $ = _c(15);
  const {
    handleEnter,
    isTooLong,
    isExpanded,
    toggleIsExpanded,
    comment
  } = t0;
  const iconName = isExpanded ? "chevronUp" : "chevronDown";
  if (isExpanded || !isTooLong) {
    let t1;
    if ($[0] !== iconName || $[1] !== isTooLong || $[2] !== toggleIsExpanded) {
      t1 = isTooLong && <IconButton kind="plain" size="default" iconName={iconName} onClick={toggleIsExpanded} className={styles.showMoreButton} />;
      $[0] = iconName;
      $[1] = isTooLong;
      $[2] = toggleIsExpanded;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== comment || $[5] !== t1) {
      t2 = <>{comment}{t1}</>;
      $[4] = comment;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  let t1;
  if ($[7] !== iconName || $[8] !== isTooLong) {
    t1 = isTooLong && <IconButton kind="plain" size="default" iconName={iconName} className={styles.showMoreButton} />;
    $[7] = iconName;
    $[8] = isTooLong;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== comment || $[11] !== handleEnter || $[12] !== t1 || $[13] !== toggleIsExpanded) {
    t2 = <div className={styles.clickArea} onClick={toggleIsExpanded} onKeyUp={handleEnter} role="button" tabIndex={0}>{comment}{t1}</div>;
    $[10] = comment;
    $[11] = handleEnter;
    $[12] = t1;
    $[13] = toggleIsExpanded;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}
function CommentWrapper({
  comment,
  id
}: {
  id: string;
  comment: string | undefined;
}) {
  const {
    isExpanded,
    toggleExpanded
  } = useDonationFeedItemContext(id);
  const [isTooLong, setIsTooLong] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    requestAnimationFrame(() => {
      const element = ref.current;
      if (!element) {
        return;
      }
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
      const neededLines = Math.ceil(element.scrollHeight / Math.round(lineHeight));
      setIsTooLong(neededLines > COMMENT_PREVIEW_LINES);
    });
  }, [comment]);
  const handleEnter: KeyboardEventHandler<HTMLInputElement> = useCallback(event => {
    if (event.key !== Keys.Enter) return;
    toggleExpanded();
  }, [toggleExpanded]);
  if (!comment) return null;
  return <div data-testid={`comment-${id}`} ref={ref} className={classNames(styles.comment, {
    [styles.expanded!]: isExpanded
  })} style={{
    '--max-preview-lines': COMMENT_PREVIEW_LINES
  } as CSSProperties}>
      <CommentInner handleEnter={handleEnter} isTooLong={isTooLong} isExpanded={isExpanded} toggleIsExpanded={toggleExpanded} comment={comment} />
    </div>;
}
function Comment({
  comment,
  id
}: {
  id: string;
  comment: string | undefined;
}) {
  return <div className={classNames(styles.commentContainer, {
    [styles.noComment!]: !comment
  })}>
      <CommentWrapper comment={comment} id={id} />
    </div>;
}
function Timestamp(t0) {
  const $ = _c(3);
  const {
    timestamp
  } = t0;
  if (!timestamp) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <LoadingSkeleton width="5.7rem" height="1.2615rem" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== timestamp) {
    t1 = <div className={styles.timestamp}>{timestamp}</div>;
    $[1] = timestamp;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function Header(t0) {
  const $ = _c(10);
  const {
    header
  } = t0;
  const [isTruncated, setIsTruncated] = useState(false);
  const ref = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const element = ref.current;
      if (!element) {
        return;
      }
      setIsTruncated(element.scrollWidth > Math.round(element.clientWidth));
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== header) {
    t2 = [header];
    $[1] = header;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (isTruncated) {
    let t3;
    if ($[3] !== header) {
      t3 = <div className={styles.header}>{header}</div>;
      $[3] = header;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== header || $[6] !== t3) {
      t4 = <Tooltip content={header}>{t3}</Tooltip>;
      $[5] = header;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t3;
  if ($[8] !== header) {
    t3 = <div ref={ref} className={styles.header}>{header}</div>;
    $[8] = header;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function DetailsSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <LayoutStack className={styles.skeletonContainer} space="200"><LoadingSkeleton width="5.7rem" height="1.3rem" /><LoadingSkeleton width="5.7rem" height="1.1rem" /></LayoutStack>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function DetailsContainer(t0) {
  const $ = _c(14);
  const {
    header,
    timestamp,
    comment,
    cta,
    skeleton,
    id
  } = t0;
  if (skeleton) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <DetailsSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (cta) {
    let t1;
    if ($[1] !== cta) {
      t1 = <div className={styles.secondSlotWrapper}>{cta}</div>;
      $[1] = cta;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== header) {
    t1 = <Header header={header} />;
    $[3] = header;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== timestamp) {
    t2 = <Timestamp timestamp={timestamp} />;
    $[5] = timestamp;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== comment || $[8] !== id) {
    t3 = <Comment comment={comment} id={id} />;
    $[7] = comment;
    $[8] = id;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t3) {
    t4 = <LayoutStack space="none" className={styles.secondSlotWrapper}>{t1}{t2}{t3}</LayoutStack>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function SecondSlot(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
SecondSlot.DetailsContainer = DetailsContainer;
const DonationFeedItem = forwardRef(function DonationFeedItem(t0, ref) {
  const $ = _c(11);
  const {
    children,
    className,
    style
  } = t0;
  let t1;
  if ($[0] !== style) {
    t1 = {
      ...style,
      overflow: "visible"
    };
    $[0] = style;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== className) {
    t2 = classNames(styles.card, className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== ref || $[6] !== t2) {
    t3 = <Card className={t2} ref={ref}>{children}</Card>;
    $[4] = children;
    $[5] = ref;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <div style={t1}>{t3}</div>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}) as DonationFeedItemComponent;
type DonationFeedItemContextType = {
  expanded: Record<string, boolean>;
  toggle: (id: string) => void;
};
const DonationFeedItemContext = createContext<DonationFeedItemContextType>({
  expanded: {},
  toggle: () => {}
});
function DonationFeedItemContextProvider(t0) {
  const $ = _c(7);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [expanded, setExpanded] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = id => {
      setExpanded(prev => ({
        ...prev,
        [id]: !prev[id]
      }));
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const toggle = t2;
  let t3;
  if ($[2] !== expanded) {
    t3 = {
      expanded,
      toggle
    };
    $[2] = expanded;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children || $[5] !== t3) {
    t4 = <DonationFeedItemContext.Provider value={t3}>{children}</DonationFeedItemContext.Provider>;
    $[4] = children;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
DonationFeedItem.FirstSlot = FirstSlot;
DonationFeedItem.SecondSlot = SecondSlot;
DonationFeedItem.ContextProvider = DonationFeedItemContextProvider;
export default DonationFeedItem;